import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Section } from "../commercial-dashboard/DashboardBits";
import {
  CHANNEL_COLORS,
  CHANNELS,
  CM_CHANNELS,
  MONTHS,
  MONTHS_SHORT,
  PM_CHANNELS,
  aggregate,
  eur,
  eurSigned,
  groupBy,
  isPaidMedia,
  peopleLabel,
  pct,
  pp,
  subformatLabel,
  toneOfAmount,
  toneOfCompliance,
  toneOfExecution,
  toneOfMarginDiff,
} from "./controlBoardModel";
import { Caret, ChannelSwatch, Note, Num, Signal } from "./ControlBoardBits";
import { BoardData } from "./boardData";
import { ControlBoardRow } from "../../types/controlBoard";

interface Props {
  data: BoardData;
  /** Filas del periodo elegido. */
  rows: ControlBoardRow[];
  /** Filas del año (para mes a mes). */
  allRows: ControlBoardRow[];
  month: number;
}

const people = (
  list: ControlBoardRow[],
  pick: (r: ControlBoardRow) => string[]
) => peopleLabel([...new Set(list.flatMap(pick))]);

/** Técnicos, mes a mes y consulta de campañas de un cliente. */
const ClientDetailTables: React.FC<Props> = ({
  data,
  rows,
  allRows,
  month,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const cm = rows.filter((r) => r.type === "CM");
  const pm = rows.filter((r) => r.type === "PM");
  const cmTotals = aggregate(cm);
  const pmTotals = aggregate(pm);
  const sub = { backgroundColor: theme.palette.grey[50] };

  const cmChannels = CM_CHANNELS.filter((c) =>
    cm.some((r) => r.channel === c && r.sale > 0)
  );
  const byChannelTech = cmChannels.map((c) => {
    const t = aggregate(cm.filter((r) => r.channel === c));
    return { c, t };
  });
  const best = [...byChannelTech].sort(
    (a, b) => b.t.marginOverSold - a.t.marginOverSold
  )[0];
  const worst = [...byChannelTech].sort(
    (a, b) => a.t.marginOverSold - b.t.marginOverSold
  )[0];

  return (
    <>
      <Section
        title="Técnico compra de medios"
        subtitle={`${people(cm, (r) => r.technicians)} compra los formatos con margen. Se compara el margen vendido en el briefing con el que sale de la compra.`}
      >
        {cm.length > 0 && (
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            En total la compra{" "}
            {cmTotals.marginOverSold >= 0 ? "mejora" : "empeora"} el margen
            vendido en <strong>{eur(Math.abs(cmTotals.marginOverSold))}</strong>{" "}
            ({pct(cmTotals.soldMargin)} vendido frente a {pct(cmTotals.margin)}{" "}
            real).
            {best && best.t.marginOverSold > 0
              ? ` Donde más se gana es en ${best.c} (${eurSigned(best.t.marginOverSold)}).`
              : ""}
            {worst && worst !== best && worst.t.marginOverSold < 0
              ? ` Donde más se pierde es en ${worst.c} (${eurSigned(worst.t.marginOverSold)}).`
              : ""}
          </Typography>
        )}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Formato</TableCell>
                <TableCell>Técnico</TableCell>
                <Num>Inversión prevista</Num>
                <Num>Inversión gestionada</Num>
                <Num>Margen briefing</Num>
                <Num>Margen real</Num>
                <Num>Diferencia</Num>
                <Num>Efecto en €</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {cmChannels.map((c) => {
                const list = cm.filter((r) => r.channel === c);
                const t = aggregate(list);
                const key = `tec:${c}`;
                const subs = [...groupBy(list, (r) => r.subformat).entries()]
                  .map(([s, l]) => ({ s, l, t: aggregate(l) }))
                  .filter((o) => o.t.sale > 0)
                  .sort((a, b) => b.t.sale - a.t.sale);
                return (
                  <React.Fragment key={c}>
                    <TableRow
                      hover
                      onClick={() => toggle(key)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Caret
                          open={open.has(key)}
                          onClick={() => toggle(key)}
                        />
                        <ChannelSwatch channel={c} />
                        <strong>{c}</strong>
                      </TableCell>
                      <TableCell>
                        {people(list, (r) => r.technicians)}
                      </TableCell>
                      <Num muted>{eur(t.plannedSale)}</Num>
                      <Num>{eur(t.sale)}</Num>
                      <Num muted>{pct(t.soldMargin, 1)}</Num>
                      <Num>
                        <Signal
                          text={pct(t.margin, 1)}
                          tone={toneOfMarginDiff(t.margin - t.soldMargin)}
                        />
                      </Num>
                      <Num>
                        <Signal
                          text={pp(t.margin - t.soldMargin)}
                          tone={toneOfMarginDiff(t.margin - t.soldMargin)}
                        />
                      </Num>
                      <Num>
                        <Signal
                          text={eurSigned(t.marginOverSold)}
                          tone={toneOfAmount(t.marginOverSold, t.profit)}
                        />
                      </Num>
                    </TableRow>
                    {open.has(key) &&
                      subs.map((o) => (
                        <TableRow key={`${c}-${o.s}`} sx={sub}>
                          <TableCell sx={{ pl: 7 }}>
                            {subformatLabel(o.s)}
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>
                            {people(o.l, (r) => r.technicians)}
                          </TableCell>
                          <Num muted>{eur(o.t.plannedSale)}</Num>
                          <Num>{eur(o.t.sale)}</Num>
                          <Num muted>{pct(o.t.soldMargin, 1)}</Num>
                          <Num>{pct(o.t.margin, 1)}</Num>
                          <Num>
                            <Signal
                              text={pp(o.t.margin - o.t.soldMargin)}
                              tone={toneOfMarginDiff(
                                o.t.margin - o.t.soldMargin
                              )}
                            />
                          </Num>
                          <Num>
                            <Signal
                              text={eurSigned(o.t.marginOverSold)}
                              tone={toneOfAmount(
                                o.t.marginOverSold,
                                o.t.profit
                              )}
                            />
                          </Num>
                        </TableRow>
                      ))}
                  </React.Fragment>
                );
              })}
              {cm.length > 0 && (
                <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <Num>{eur(cmTotals.plannedSale)}</Num>
                  <Num>{eur(cmTotals.sale)}</Num>
                  <Num>{pct(cmTotals.soldMargin, 1)}</Num>
                  <Num>{pct(cmTotals.margin, 1)}</Num>
                  <Num>{pp(cmTotals.margin - cmTotals.soldMargin)}</Num>
                  <Num>{eurSigned(cmTotals.marginOverSold)}</Num>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        {cm.length === 0 && <Note>No hay compra de medios en el periodo.</Note>}
      </Section>

      <Section
        title="Técnico Paid Media"
        subtitle={
          pm.length
            ? `${people(pm, (r) => r.technicians)}. Paid Media se mide por la inversión ejecutada frente a la prevista en el briefing.`
            : "No hay Paid Media en el periodo."
        }
      >
        {pm.length > 0 && (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Formato</TableCell>
                  <TableCell>Técnico</TableCell>
                  <Num>Inversión prevista</Num>
                  <Num>Inversión ejecutada</Num>
                  <Num>Diferencia</Num>
                  <Num>Ejecución</Num>
                </TableRow>
              </TableHead>
              <TableBody>
                {PM_CHANNELS.filter((c) => pm.some((r) => r.channel === c)).map(
                  (c) => {
                    const list = pm.filter((r) => r.channel === c);
                    const t = aggregate(list);
                    const key = `pm:${c}`;
                    const subs = [
                      ...groupBy(list, (r) => r.subformat).entries(),
                    ]
                      .map(([s, l]) => ({ s, l, t: aggregate(l) }))
                      .sort((a, b) => b.t.sale - a.t.sale);
                    return (
                      <React.Fragment key={c}>
                        <TableRow
                          hover
                          onClick={() => toggle(key)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Caret
                              open={open.has(key)}
                              onClick={() => toggle(key)}
                            />
                            <ChannelSwatch channel={c} />
                            <strong>{c}</strong>
                          </TableCell>
                          <TableCell>
                            {people(list, (r) => r.technicians)}
                          </TableCell>
                          <Num muted>{eur(t.plannedSale)}</Num>
                          <Num sx={{ fontWeight: 600 }}>{eur(t.sale)}</Num>
                          <Num>{eurSigned(t.sale - t.plannedSale)}</Num>
                          <Num>
                            <Signal
                              text={pct(t.compliance, 0)}
                              tone={toneOfExecution(t.compliance)}
                            />
                          </Num>
                        </TableRow>
                        {open.has(key) &&
                          subs.map((o) => (
                            <TableRow key={`${c}-${o.s}`} sx={sub}>
                              <TableCell sx={{ pl: 7 }}>
                                {subformatLabel(o.s)}
                              </TableCell>
                              <TableCell sx={{ color: "text.secondary" }}>
                                {people(o.l, (r) => r.technicians)}
                              </TableCell>
                              <Num muted>{eur(o.t.plannedSale)}</Num>
                              <Num>{eur(o.t.sale)}</Num>
                              <Num>{eurSigned(o.t.sale - o.t.plannedSale)}</Num>
                              <Num>
                                <Signal
                                  text={pct(o.t.compliance, 0)}
                                  tone={toneOfExecution(o.t.compliance)}
                                />
                              </Num>
                            </TableRow>
                          ))}
                      </React.Fragment>
                    );
                  }
                )}
                <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <Num>{eur(pmTotals.plannedSale)}</Num>
                  <Num>{eur(pmTotals.sale)}</Num>
                  <Num>{eurSigned(pmTotals.sale - pmTotals.plannedSale)}</Num>
                  <Num>
                    <Signal
                      text={pct(pmTotals.compliance, 0)}
                      tone={toneOfExecution(pmTotals.compliance)}
                    />
                  </Num>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </Section>

      <MonthByMonth data={data} rows={allRows} highlight={month} />
      <Campaigns rows={allRows} defaultMonth={month} />
    </>
  );
};

const MonthByMonth: React.FC<{
  data: BoardData;
  rows: ControlBoardRow[];
  highlight: number;
}> = ({ data, rows, highlight }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<Set<number>>(new Set());
  const byMonth = groupBy(rows, (r) => r.month);
  const months = Array.from({ length: data.lastMonth }, (_, i) => i + 1);
  const all = aggregate(rows.filter((r) => r.month <= data.lastMonth));

  return (
    <Section
      title="Mes a mes"
      subtitle="Previsión del briefing frente a lo facturado. Pulsa un mes para ver cada canal."
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Mes</TableCell>
              <Num>Fact. prevista</Num>
              <Num>Fact. real</Num>
              <Num>Cumpl.</Num>
              <Num>B. bruto previsto</Num>
              <Num>B. bruto real</Num>
              <Num>Cumpl.</Num>
              <Num>Margen briefing</Num>
              <Num>Margen real</Num>
              <Num>Margen del margen</Num>
              <TableCell>Reparto real</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {months.map((m) => {
              const list = byMonth.get(m) ?? [];
              if (!list.length)
                return (
                  <TableRow key={m}>
                    <TableCell>{MONTHS[m]}</TableCell>
                    <TableCell colSpan={10} sx={{ color: "text.secondary" }}>
                      Sin campañas
                    </TableCell>
                  </TableRow>
                );
              const t = aggregate(list);
              const isOpen = open.has(m);
              return (
                <React.Fragment key={m}>
                  <TableRow
                    hover
                    onClick={() =>
                      setOpen((prev) => {
                        const next = new Set(prev);
                        if (next.has(m)) next.delete(m);
                        else next.add(m);
                        return next;
                      })
                    }
                    sx={{
                      cursor: "pointer",
                      boxShadow:
                        highlight === m
                          ? `inset 3px 0 0 ${theme.palette.text.primary}`
                          : undefined,
                    }}
                  >
                    <TableCell>
                      <Caret open={isOpen} />
                      <strong>{MONTHS[m]}</strong>
                    </TableCell>
                    <Num muted>{eur(t.plannedSale)}</Num>
                    <Num>{eur(t.sale)}</Num>
                    <Num>
                      <Signal
                        text={pct(t.compliance, 0)}
                        tone={toneOfCompliance(t.compliance)}
                      />
                    </Num>
                    <Num muted>{eur(t.plannedProfit)}</Num>
                    <Num>{eur(t.profit)}</Num>
                    <Num>
                      {t.plannedProfit ? (
                        <Signal
                          text={pct(t.profitCompliance, 0)}
                          tone={toneOfCompliance(t.profitCompliance)}
                        />
                      ) : (
                        "—"
                      )}
                    </Num>
                    <Num muted>{pct(t.soldMargin, 1)}</Num>
                    <Num>{pct(t.margin, 1)}</Num>
                    <Num>
                      <Signal
                        text={eurSigned(t.marginOverSold)}
                        tone={toneOfAmount(t.marginOverSold, t.profit)}
                      />
                    </Num>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          height: 10,
                          minWidth: 120,
                          borderRadius: 5,
                          overflow: "hidden",
                          backgroundColor: theme.palette.grey[200],
                        }}
                      >
                        {CHANNELS.map((c) => {
                          const v = list
                            .filter((r) => r.channel === c)
                            .reduce((s, r) => s + r.sale, 0);
                          return v > 0 ? (
                            <Box
                              key={c}
                              title={c}
                              sx={{
                                width: `${(v / (t.sale || 1)) * 100}%`,
                                backgroundColor: CHANNEL_COLORS[c],
                              }}
                            />
                          ) : null;
                        })}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {isOpen &&
                    CHANNELS.map((c) => {
                      const l = list.filter((r) => r.channel === c);
                      if (!l.length) return null;
                      const ct = aggregate(l);
                      const pmc = isPaidMedia(c);
                      return (
                        <TableRow
                          key={`${m}-${c}`}
                          sx={{ backgroundColor: theme.palette.grey[50] }}
                        >
                          <TableCell sx={{ pl: 5 }}>
                            <ChannelSwatch channel={c} />
                            {c}
                          </TableCell>
                          <Num muted>{eur(ct.plannedSale)}</Num>
                          <Num>{eur(ct.sale)}</Num>
                          <Num>
                            <Signal
                              text={pct(ct.compliance, 0)}
                              tone={toneOfCompliance(ct.compliance)}
                            />
                          </Num>
                          <Num muted>{pmc ? "—" : eur(ct.plannedProfit)}</Num>
                          <Num>{pmc ? "—" : eur(ct.profit)}</Num>
                          <Num>
                            {pmc || !ct.plannedProfit
                              ? "—"
                              : pct(ct.profitCompliance, 0)}
                          </Num>
                          <Num muted>{pmc ? "—" : pct(ct.soldMargin, 1)}</Num>
                          <Num>{pmc ? "—" : pct(ct.margin, 1)}</Num>
                          <Num>{pmc ? "—" : eurSigned(ct.marginOverSold)}</Num>
                          <TableCell />
                        </TableRow>
                      );
                    })}
                </React.Fragment>
              );
            })}
            <TableRow sx={{ "& td": { fontWeight: 700 } }}>
              <TableCell>Total</TableCell>
              <Num>{eur(all.plannedSale)}</Num>
              <Num>{eur(all.sale)}</Num>
              <Num>{pct(all.compliance, 0)}</Num>
              <Num>{eur(all.plannedProfit)}</Num>
              <Num>{eur(all.profit)}</Num>
              <Num>{pct(all.profitCompliance, 0)}</Num>
              <Num>{pct(all.soldMargin, 1)}</Num>
              <Num>{pct(all.margin, 1)}</Num>
              <Num>{eurSigned(all.marginOverSold)}</Num>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      <Note>
        El mes en curso va con datos hasta ayer ({data.dataUntil}) frente a todo
        lo previsto del mes.
      </Note>
    </Section>
  );
};

const LIMIT = 400;

const Campaigns: React.FC<{
  rows: ControlBoardRow[];
  defaultMonth: number;
}> = ({ rows, defaultMonth }) => {
  const [monthFilter, setMonthFilter] = useState<number | "">("");
  const [channel, setChannel] = useState("");
  const [subformat, setSubformat] = useState("");
  const [tech, setTech] = useState("");
  const [query, setQuery] = useState("");
  const [onlyLosing, setOnlyLosing] = useState(false);
  const month = monthFilter === "" ? defaultMonth : monthFilter;

  const options = useMemo(
    () => ({
      months: [...new Set(rows.map((r) => r.month))].sort((a, b) => a - b),
      channels: CHANNELS.filter((c) => rows.some((r) => r.channel === c)),
      subformats: [
        ...new Set(
          rows
            .filter((r) => !channel || r.channel === channel)
            .map((r) => r.subformat ?? "")
        ),
      ].sort(),
      techs: [...new Set(rows.flatMap((r) => r.technicians))].sort(),
    }),
    [rows, channel]
  );

  const q = query.trim().toLowerCase();
  const filtered = rows
    .filter(
      (r) =>
        (!month || r.month === month) &&
        (!channel || r.channel === channel) &&
        (!subformat || (r.subformat ?? "") === subformat) &&
        (!tech || r.technicians.includes(tech)) &&
        (!q || `${r.lineName} ${r.briefingName}`.toLowerCase().includes(q)) &&
        (!onlyLosing ||
          (r.soldMargin !== null &&
            r.sale > 0 &&
            (r.sale - r.realCost) / r.sale < r.soldMargin - 0.0005))
    )
    .sort((a, b) => a.month - b.month || b.sale - a.sale);

  return (
    <Section
      title="Consulta de campañas"
      subtitle="Línea a línea, para encontrar en qué campaña y en qué mes pasó algo."
    >
      <Stack
        direction="row"
        gap={1.5}
        flexWrap="wrap"
        alignItems="center"
        mb={1.5}
      >
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="cq-month">Mes</InputLabel>
          <Select
            labelId="cq-month"
            label="Mes"
            value={month}
            onChange={(e) => setMonthFilter(Number(e.target.value))}
          >
            <MenuItem value={0}>Todos</MenuItem>
            {options.months.map((m) => (
              <MenuItem key={m} value={m}>
                {MONTHS[m]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="cq-channel">Canal</InputLabel>
          <Select
            labelId="cq-channel"
            label="Canal"
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value);
              setSubformat("");
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            {options.channels.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="cq-sub">Subformato</InputLabel>
          <Select
            labelId="cq-sub"
            label="Subformato"
            value={subformat}
            onChange={(e) => setSubformat(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {options.subformats.map((s) => (
              <MenuItem key={s} value={s}>
                {subformatLabel(s || null)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="cq-tech">Técnico</InputLabel>
          <Select
            labelId="cq-tech"
            label="Técnico"
            value={tech}
            onChange={(e) => setTech(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {options.techs.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Línea o briefing"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={onlyLosing}
              onChange={(e) => setOnlyLosing(e.target.checked)}
            />
          }
          label="Solo las que pierden margen"
        />
        <Typography variant="caption" color="text.secondary">
          {filtered.length.toLocaleString("es-ES")} líneas
          {filtered.length > LIMIT
            ? ` · se muestran las ${LIMIT} primeras`
            : ""}
        </Typography>
      </Stack>
      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Mes</TableCell>
              <TableCell>Marca</TableCell>
              <TableCell>Línea</TableCell>
              <TableCell>Canal</TableCell>
              <TableCell>Subformato</TableCell>
              <TableCell>Técnico</TableCell>
              <TableCell>Modelo</TableCell>
              <Num>Fact. prevista</Num>
              <Num>Fact. real</Num>
              <Num>Margen briefing</Num>
              <Num>Margen real</Num>
              <Num>Diferencia</Num>
              <Num>Margen del margen</Num>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.slice(0, LIMIT).map((r) => {
              const real = r.sale ? (r.sale - r.realCost) / r.sale : NaN;
              const withMargin = r.soldMargin !== null;
              return (
                <TableRow key={`${r.lineId}-${r.month}`} hover>
                  <TableCell>{MONTHS_SHORT[r.month]}</TableCell>
                  <TableCell>{r.company}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 320,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={`${r.lineName} · ${r.briefingName}`}
                  >
                    {r.lineName}
                  </TableCell>
                  <TableCell>{r.channel}</TableCell>
                  <TableCell>{subformatLabel(r.subformat)}</TableCell>
                  <TableCell>{peopleLabel(r.technicians)}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {r.objective}
                  </TableCell>
                  <Num muted>{eur(r.plannedSale)}</Num>
                  <Num>{eur(r.sale)}</Num>
                  <Num muted>
                    {withMargin ? pct(r.soldMargin as number, 1) : "—"}
                  </Num>
                  <Num>{withMargin && r.sale ? pct(real, 1) : "—"}</Num>
                  <Num>
                    {withMargin && r.sale ? (
                      <Signal
                        text={pp(real - (r.soldMargin as number))}
                        tone={toneOfMarginDiff(real - (r.soldMargin as number))}
                      />
                    ) : (
                      "—"
                    )}
                  </Num>
                  <Num>
                    {withMargin && r.sale
                      ? eurSigned(
                          r.sale -
                            r.realCost -
                            r.sale * (r.soldMargin as number)
                        )
                      : "—"}
                  </Num>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Section>
  );
};

export default ClientDetailTables;
